/**
 * React binding for the staff realtime connection. One connection per signed-in staff session, started
 * and stopped with it. Features subscribe by channel; the layer never decides what an event means.
 * Guests have no stream: with no staff session this provider stays idle and opens nothing.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { staffApi } from '@/api/instances';
import { useStaffSession } from '@/auth/react';
import { canUseLiveUpdates } from '@/auth/roles';
import { staffSessionStore } from '@/auth/staff-session';
import { createRealtimeClient } from './client';
import type { ConnectionState, RealtimeChannel, RealtimeEvent, ResyncEvent } from './types';

type Payload = RealtimeEvent | ResyncEvent | undefined;
type Handler = (payload: Payload) => void;

interface RealtimeContextValue {
  state: ConnectionState;
  subscribe: (channel: RealtimeChannel, handler: Handler) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  state: 'idle',
  subscribe: () => () => undefined,
});

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const session = useStaffSession();
  // Only roles with a live screen get a stream; the others stay 'idle' and see no banner.
  const token = session && canUseLiveUpdates(session.user.role) ? session.token : null;
  const [state, setState] = useState<ConnectionState>('idle');
  const handlers = useRef(new Map<RealtimeChannel, Set<Handler>>());

  const subscribe = useMemo<RealtimeContextValue['subscribe']>(
    () => (channel, handler) => {
      const set = handlers.current.get(channel) ?? new Set<Handler>();
      set.add(handler);
      handlers.current.set(channel, set);
      return () => {
        set.delete(handler);
      };
    },
    [],
  );

  useEffect(() => {
    if (!token) {
      setState('idle');
      return;
    }
    const emit = (channel: RealtimeChannel, payload?: Payload) => {
      handlers.current.get(channel)?.forEach((handler) => handler(payload));
    };
    const client = createRealtimeClient({
      api: staffApi,
      getSession: () => staffSessionStore.get(),
      onEvent: (event) => emit(event.type as RealtimeChannel, event),
      onResync: (event) => emit('stream.resync', event),
      onReady: () => emit('stream.ready'),
      onStateChange: setState,
    });
    client.start();
    return () => client.stop();
  }, [token]);

  const value = useMemo(() => ({ state, subscribe }), [state, subscribe]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeState(): ConnectionState {
  return useContext(RealtimeContext).state;
}

/** Runs `handler` for every event on `channel` while the component is mounted. */
export function useRealtimeEvent(
  channel: RealtimeChannel,
  handler: (payload: Payload) => void,
): void {
  const { subscribe } = useContext(RealtimeContext);
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(() => subscribe(channel, (payload) => latest.current(payload)), [channel, subscribe]);
}
